import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FiTool, FiUsers, FiPackage, FiTrendingUp, FiShoppingCart, FiFileText, FiSettings } from 'react-icons/fi';

const FeaturesSection = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: <FiTrendingUp className="w-12 h-12" />,
      moduleKey: 'sales',
      emoji: '💰',
      image: 'https://images.unsplash.com/photo-1650327034581-1711a15a5430?w=800&q=80',
      color: 'from-orange-500 to-red-500',
      featureCount: '12+'
    },
    {
      icon: <FiPackage className="w-12 h-12" />,
      moduleKey: 'inventory',
      emoji: '📦',
      image: 'https://images.pexels.com/photos/31112250/pexels-photo-31112250.jpeg?w=800&q=80',
      color: 'from-green-500 to-teal-500',
      featureCount: '28'
    },
    {
      icon: <FiUsers className="w-12 h-12" />,
      moduleKey: 'crm',
      emoji: '🤝',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80',
      color: 'from-cyan-500 to-blue-500',
      featureCount: '10'
    },
    {
      icon: <FiSettings className="w-12 h-12" />,
      moduleKey: 'production',
      emoji: '🏭',
      image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80',
      color: 'from-sky-500 to-blue-600',
      featureCount: '13'
    },
    {
      icon: <FiFileText className="w-12 h-12" />,
      moduleKey: 'accounting',
      emoji: '📊',
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80',
      color: 'from-red-500 to-pink-500',
      featureCount: '14'
    },
    {
      icon: <FiTrendingUp className="w-12 h-12" />,
      moduleKey: 'finance',
      emoji: '💳',
      image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&q=80',
      color: 'from-emerald-500 to-teal-500',
      featureCount: '14'
    },
    {
      icon: <FiUsers className="w-12 h-12" />,
      moduleKey: 'hr',
      emoji: '👥',
      image: 'https://images.pexels.com/photos/3733584/pexels-photo-3733584.jpeg?w=800&q=80',
      color: 'from-purple-500 to-pink-500',
      featureCount: '9'
    },
    {
      icon: <FiTool className="w-12 h-12" />,
      moduleKey: 'service',
      emoji: '🔧',
      image: 'https://images.unsplash.com/photo-1580982324076-d95230549339?w=800&q=80',
      color: 'from-blue-500 to-cyan-500',
      featureCount: '12'
    },
    {
      icon: <FiFileText className="w-12 h-12" />,
      moduleKey: 'tender',
      emoji: '⚖️',
      image: 'https://images.unsplash.com/photo-1648822145238-16e13a1591da?w=800&q=80',
      color: 'from-yellow-500 to-orange-500',
      featureCount: '8'
    },
    {
      icon: <FiShoppingCart className="w-12 h-12" />,
      moduleKey: 'purchasing',
      emoji: '🛒',
      image: 'https://images.unsplash.com/photo-1560250163-17506787d971?w=800&q=80',
      color: 'from-indigo-500 to-blue-500',
      featureCount: '6'
    },
    {
      icon: <FiFileText className="w-12 h-12" />,
      moduleKey: 'tasks',
      emoji: '✅',
      image: 'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=800&q=80',
      color: 'from-violet-500 to-purple-500',
      featureCount: '6'
    },
    {
      icon: <FiFileText className="w-12 h-12" />,
      moduleKey: 'assets',
      emoji: '💼',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80',
      color: 'from-gray-500 to-slate-600',
      featureCount: '6'
    },
    {
      icon: <FiPackage className="w-12 h-12" />,
      moduleKey: 'office',
      emoji: '🏢',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
      color: 'from-indigo-500 to-purple-500',
      featureCount: '7',
      comingSoon: true
    }
  ];

  return (
    <section id="features" className="py-20 bg-gray-50 dark:bg-surface-900" aria-label={t('landing.featuresSection.ariaLabel')}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-extrabold text-gray-900 dark:text-surface-50 mb-4" data-testid="features-title">
            {t('landing.features.title')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-surface-400 mb-2" data-testid="features-subtitle">
            {t('landing.features.subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-6 max-w-5xl mx-auto">
            <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">✅ {t('landing.features.badges.multiTenant')}</span>
            <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">✅ {t('landing.features.badges.multiCurrency')}</span>
            <span className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-semibold">✅ {t('landing.features.badges.rbac')}</span>
            <span className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-semibold">✅ {t('landing.features.badges.webSocket')}</span>
            <span className="bg-pink-100 text-pink-800 px-4 py-2 rounded-full text-sm font-semibold">✅ {t('landing.features.badges.realTime')}</span>
            <span className="bg-indigo-100 text-indigo-800 px-4 py-2 rounded-full text-sm font-semibold">✅ {t('landing.features.badges.restApi')}</span>
            <span className="bg-cyan-100 text-cyan-800 px-4 py-2 rounded-full text-sm font-semibold">✅ {t('landing.features.badges.jwtAuth')}</span>
            <span className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-semibold">✅ {t('landing.features.badges.pdfGeneration')}</span>
            <span className="bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-semibold">✅ {t('landing.features.badges.emailIntegration')}</span>
            <span className="bg-violet-100 text-violet-800 px-4 py-2 rounded-full text-sm font-semibold">✅ {t('landing.features.badges.pbx')}</span>
            <span className="bg-teal-100 text-teal-800 px-4 py-2 rounded-full text-sm font-semibold">✅ {t('landing.features.badges.softphone')}</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const title = t(`shared.moduleNames.${feature.moduleKey}`);
            const description = t(`landing.features.modules.${feature.moduleKey}.description`);
            const details = t(`landing.features.modules.${feature.moduleKey}.details`, { returnObjects: true });

            return (
              <article
                key={index}
                className="bg-white dark:bg-surface-900 rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition duration-300 transform hover:-translate-y-2 flex flex-col"
                data-testid={`feature-card-${index}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={feature.image}
                    alt={t('landing.featuresSection.imageAlt', { title })}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-80`}></div>
                  <div className="absolute inset-0 flex items-center justify-center text-white">
                    {feature.icon}
                  </div>
                  {feature.comingSoon && (
                    <div className="absolute top-3 right-3 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded">
                      {t('landing.features.comingSoon')}
                    </div>
                  )}
                  <div className="absolute bottom-3 left-3 bg-white dark:bg-surface-900/90 text-gray-800 dark:text-surface-100 text-xs font-bold px-2 py-1 rounded">
                    {t('landing.features.featureCount', { count: feature.featureCount })}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-surface-50 mb-3">{feature.emoji} {title}</h3>
                  <p className="text-gray-600 dark:text-surface-400 leading-relaxed mb-4 flex-1">{description}</p>
                  {details && Array.isArray(details) && (
                    <div className="border-t pt-4 mt-4">
                      <p className="text-sm font-semibold text-gray-700 dark:text-surface-200 mb-2">{t('landing.features.featuresLabel')}</p>
                      <ul className="text-sm text-gray-600 dark:text-surface-400 space-y-1">
                        {details.slice(0, 4).map((detail, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-green-500 me-2">✓</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                        {details.length > 4 && (
                          <li className="text-blue-600 font-medium">{t('landing.features.moreFeaturesCount', { count: details.length - 4 })}</li>
                        )}
                      </ul>
                    </div>
                  )}
                  <Link
                    to={`/module-details/${feature.moduleKey}`}
                    className={`mt-4 w-full text-center py-2 px-4 rounded-lg font-semibold transition duration-300 bg-gradient-to-r ${feature.color} text-white hover:opacity-90`}
                  >
                    {t('landing.features.viewDetails')} →
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
